import LoadingButton from '@/common/components/base/button/loadingButton';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { ONLY_NUMBERS_WITH_SEPARATORS_RULE } from '@/common/components/base/input/textInput/textInput.rules';
import NumberInputWithWords from '@/common/components/base/numberInputWithWords/NumberInputWithWords';
import MainLayout from '@/common/components/layouts/MainLayout';
import useDebounce from '@/common/hooks/useDebounce';
import { substitutionPartsOption } from '@/common/queries/selections/substitutionParts/substitutionPartsOption';
import { requiredText } from '@/common/utils/rules/publicRules';
import { useAddSubstitutionPartQueryOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/addSubstitutionPart/addSubstitutionPartQueryOption';
import useSingleSubstitutionPartsList from '@/modules/adjusterReportModules/IncidentParticipants/queries/getSingleSubstitutionPartsList/getSingleSubstitutionPartsListQueryOption';
import { getSubstitutionPartsListQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getSubstitutionPartsList/getSubstitutionPartsListQueryOptions';
import { usePatchSubstitutionPartQueryOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchSubstitutionPart/patchSubstitutionPartQueryOption';
import { SingleSubstitutionPartFormType } from '@/modules/adjusterReportModules/IncidentParticipants/types/substitutionPartsType';
import formatNumberWithCommas from '@/modules/adjusterReportModules/IncidentParticipants/utils/formatNumberWithCommas';
import { fileDetailQueryOption } from '@/modules/FileList/queries/fileDetail/fileDetailQueryOption';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';

const SubstiutionPartsFormPage = () => {
  return (
    <MainLayout title="افزودن قطعه" header="titleHeader">
      <SubstiutionPartsForm />
    </MainLayout>
  );
};

function SubstiutionPartsForm() {
  const { id, damagedId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const partId = location.state as number;
  const debounce = useDebounce();
  const [name, setName] = useState('');

  const methods = useForm<SingleSubstitutionPartFormType>({
    mode: 'onChange',
    defaultValues: { daghi_price: '0', part_price: '0', wage_price: '0' },
  });

  const partPriceWatcher = useWatch({
    control: methods?.control,
    name: 'part_price',
  });
  const daghiPriceWatcher = useWatch({
    control: methods?.control,
    name: 'daghi_price',
  });
  const wagePriceWatcher = useWatch({
    control: methods?.control,
    name: 'wage_price',
  });

  const { mutate: addSubstitutionPart, isPending: postLoading } =
    useAddSubstitutionPartQueryOption();

  const { data: fileDetail, isSuccess } = useQuery(
    fileDetailQueryOption({ id: +id! }),
  );

  const { mutate: patchSubstitutionPart, isPending: patchLoading } =
    usePatchSubstitutionPartQueryOption();

  const { data: subsParts, isLoading: subsPartsLoading } = useQuery(
    substitutionPartsOption({
      name,
      insurance: fileDetail?.insurance!,
      enabled: isSuccess,
    }),
  );

  const { data: singleSubstitutionPartData } = useQuery(
    useSingleSubstitutionPartsList({
      id: +id!,
      damaged_id: +damagedId!,
      part_id: partId!,
      enabled: !!partId,
    }),
  );

  const { refetch: getSubstitutionPartsList } = useQuery(
    getSubstitutionPartsListQueryOptions({
      id: +id!,
      damaged_id: +damagedId!,
    }),
  );

  useEffect(() => {
    if (!singleSubstitutionPartData) return;

    methods.reset({
      accident_level: {
        label: singleSubstitutionPartData.substitution.accident_level_fa,
        value: singleSubstitutionPartData.substitution.accident_level,
      },
      daghi_price: formatNumberWithCommas(
        singleSubstitutionPartData.substitution.daghi_price ?? '',
      ),
      damaged_point: {
        label: singleSubstitutionPartData.substitution.part_name,
        value: singleSubstitutionPartData.substitution.damaged_point,
      },
      part_description:
        singleSubstitutionPartData.substitution.part_description,
      part_price: formatNumberWithCommas(
        singleSubstitutionPartData.substitution.part_price ?? '',
      ),
      wage_price: formatNumberWithCommas(
        singleSubstitutionPartData.substitution.wage_price ?? '',
      ),
    });
  }, [singleSubstitutionPartData]);

  useEffect(() => {
    if (daghiPriceWatcher || wagePriceWatcher || partPriceWatcher)
      methods.reset({
        ...methods?.getValues(),
        daghi_price: formatNumberWithCommas(
          methods?.getValues('daghi_price') ?? '0',
        ),
        wage_price: formatNumberWithCommas(
          methods?.getValues('wage_price') ?? '0',
        ),
        part_price: formatNumberWithCommas(
          methods?.getValues('part_price') ?? '0',
        ),
      });
  }, [partPriceWatcher, wagePriceWatcher, daghiPriceWatcher]);

  const addSubstitutionPartHandler = (data: SingleSubstitutionPartFormType) => {
    addSubstitutionPart(
      {
        damaged_id: +damagedId!,
        source: 'pwa',
        id: +id!,
        data: {
          ...data,
          daghi_price: Number(
            (data?.daghi_price ?? '0').toString().replace(/,/g, ''),
          ),
          part_price: Number(
            (data?.part_price ?? '0').toString().replace(/,/g, ''),
          ),
          wage_price: Number(
            (data?.wage_price ?? '0').toString().replace(/,/g, ''),
          ),
          damaged_point: data?.damaged_point?.value as number,
          accident_level: data?.accident_level?.value,
        },
      },
      {
        onSuccess: () => {
          getSubstitutionPartsList();
          methods.reset();
          navigate(-1);
        },
      },
    );
  };

  const patchSubstitutionPartHandler = (
    data: SingleSubstitutionPartFormType,
  ) => {
    patchSubstitutionPart(
      {
        damaged_id: +damagedId!,
        source: 'pwa',
        id: +id!,
        partId: +partId!,
        data: {
          ...data,
          daghi_price: Number(
            (data?.daghi_price ?? '0').toString().replace(/,/g, ''),
          ),
          part_price: Number(
            (data?.part_price ?? '0').toString().replace(/,/g, ''),
          ),
          wage_price: Number(
            (data?.wage_price ?? '0').toString().replace(/,/g, ''),
          ),
          damaged_point: data?.damaged_point?.value as number,
          accident_level: data?.accident_level?.value,
        },
      },
      {
        onSuccess: () => {
          getSubstitutionPartsList();
          methods.reset();
          navigate(-1);
        },
      },
    );
  };

  const onSubmit = (data: SingleSubstitutionPartFormType) => {
    partId
      ? patchSubstitutionPartHandler(data)
      : addSubstitutionPartHandler(data);
  };

  const handleSearchChange = (value: string) => {
    debounce(() => setName(value), 500);
  };

  return (
    <Box py={24}>
      <FormProvider {...methods}>
        <Flex flexDirection="column" gap={8} mt={24}>
          <ModalInput
            name="damaged_point"
            placeholder="انتخاب نام قطعه"
            label="نام قطعه "
            title="نام قطعه"
            options={subsParts ?? []}
            loading={subsPartsLoading}
            searchOptions={{
              onChange: handleSearchChange,
              placeholder: 'جستجو نام قطعه',
            }}
            rules={{ required: { message: requiredText, value: true } }}
          />
          <Box>
            <NumberInputWithWords
              label="قیمت (ریال)"
              name="part_price"
              placeholder="قیمت را وارد کنید"
              rules={{
                required: requiredText,
                ...ONLY_NUMBERS_WITH_SEPARATORS_RULE,
              }}
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="ارزش داغی (ریال)"
              name="daghi_price"
              placeholder="قیمت را وارد کنید"
              rules={{
                required: requiredText,
                ...ONLY_NUMBERS_WITH_SEPARATORS_RULE,
              }}
            />
          </Box>
          <ModalInput
            name="accident_level"
            placeholder="انتخاب میزان تصادف"
            label="میزان تصادف"
            title="میزان تصادف"
            options={[
              { label: 'جزئی', value: 'PARTIAL' },
              { label: 'متوسط', value: 'MEDIUM' },
              { label: 'شديد', value: 'SEVERE' },
            ]}
            loading={subsPartsLoading}
            rules={{ required: { message: requiredText, value: true } }}
          />
          <Box>
            <NumberInputWithWords
              label="اجرت تعمیرات (ریال)"
              name="wage_price"
              placeholder="قیمت را وارد کنید"
              rules={{
                required: requiredText,
                ...ONLY_NUMBERS_WITH_SEPARATORS_RULE,
              }}
            />
          </Box>
          <Box>
            <TextInput
              optionalLabel
              label="توضیحات"
              name="part_description"
              placeholder="توضیحات را وارد کنید"
            />
          </Box>
          <Box w="100%">
            <LoadingButton
              onClick={methods.handleSubmit(onSubmit)}
              loading={patchLoading || postLoading}
              fullWidth
              disabled={!methods?.formState.isValid}
            >
              ثبت و ادامه
            </LoadingButton>
          </Box>
        </Flex>
      </FormProvider>
    </Box>
  );
}

export default SubstiutionPartsFormPage;
